import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useListboxCombobox } from '../../lib/hooks/useListboxCombobox';
import { LoadingIndicator, LoadingSpinner } from '../../components/onboarding/LoadingIndicator';
import {
  Briefcase,
  Check,
  ChevronDown,
  Lock,
  Pencil,
  Search,
} from 'lucide-react';
import {
  DEFAULT_LIST_PAGE_SIZE,
  listMyProjects,
  listProjects,
  listUserAssignedProjectIds,
  listUsers,
} from '../../services/onboardingApi';
import type { BackendProjectDTO } from '../../services/onboardingTypes';
import {
  projectFromBackend,
  userFromBackend,
} from '../../services/onboardingTransforms';
import { useDebouncedValue } from '../../lib/hooks/useDebouncedValue';
import { usePaginatedList } from '../../lib/hooks/usePaginatedList';
import { useScrollPaginatedLoadMore } from '../../lib/hooks/useScrollPaginatedLoadMore';
import OnboardingStepper, {
  OnboardingStep,
} from '../../components/onboarding/OnboardingStepper';
import OnboardingSubHeader from '../../components/onboarding/OnboardingSubHeader';
import {
  OnboardingSurface,
  OnboardingNestedPanel,
} from '../../components/onboarding/Surface';
import { useAuth } from '../../contexts/AuthContext';
import {
  canEditOrgUsers,
  canUpdateUserDetails,
  getUserInitials,
  isPlatformAdminUser,
  formatPlatformRoleLabel,
  formatUserDisplayName,
  PlatformRole,
  Project,
  User,
  UserMembershipRow,
  expandUsersToMembershipRows,
  validatePersonName,
  validatePhoneNumber,
} from '../../types/onboarding';
import { Button } from '../../components/onboarding/Button';
import { Dialog } from '../../components/onboarding/Dialog';
import { Input } from '../../components/onboarding/Input';
import { Label } from '../../components/onboarding/Label';
import { twMerge as cn } from 'tailwind-merge';
import { pageRoutes } from '../../constants/routes';
import { getSessionActiveOrganizationId } from '../../utils/activeOrganizationStorage';
import {
  ActiveOrganizationNotice,
} from '../../components/onboarding/ActiveOrganizationNotice';
import { RoleMenuOption } from '../../components/onboarding/RoleMenuOption';
import {
  ORG_MEMBERSHIP_ROLE_OPTIONS,
  PLATFORM_ROLE_OPTIONS,
} from '../../constants/platformRoleOptions';
const EDIT_USER_STEPS: OnboardingStep[] = [
  {
    id: 1,
    title: 'Select Membership',
    description: 'Choose the organization membership you want to edit.',
  },
  {
    id: 2,
    title: 'Edit Details',
    description: 'Update organization, role, or contact information.',
  },
  {
    id: 3,
    title: 'Review & Save',
    description: 'Review changes and confirm the update.',
  },
];

// Self-edit (Maintainer/Developer) skips the user-picker and only exposes
// the two profile fields the doc allows non-admins to change: last name and
// phone. Steps map to: 1 = Edit Profile, 2 = Review & Save.
const SELF_EDIT_STEPS: OnboardingStep[] = [
  {
    id: 1,
    title: 'Edit Profile',
    description: 'Update your last name and phone number.',
  },
  {
    id: 2,
    title: 'Review & Save',
    description: 'Review changes and confirm the update.',
  },
];

interface EditUserFormData {
  userId: string;
  organization: string;
  platformRole: PlatformRole;
  firstName: string;
  lastName: string;
  phone: string;
}

type EditUserFormErrors = Partial<
  Record<keyof EditUserFormData | 'userId', string>
>;

const EDIT_COMBO_TRIGGER_CLASS =
  'flex h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-left items-center justify-between hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-edit-light focus:border-transparent';

function normalizeProjectId(id: string | number): string {
  return String(id);
}

function EditUser() {
  const navigate = useNavigate();
  const {
    currentUser,
    organizations,
    ensureOrganizations,
    updateUser,
    updateProject,
    removeOrgUser,
    isLoading,
  } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMembershipRow, setSelectedMembershipRow] =
    useState<UserMembershipRow | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<EditUserFormData>({
    userId: '',
    organization: '',
    platformRole: 'developer',
    firstName: '',
    lastName: '',
    phone: '',
  });
  const [errors, setErrors] = useState<EditUserFormErrors>({});
  const clearFieldErrors = useCallback(
    (...fields: (keyof EditUserFormErrors)[]) => {
      setErrors((prev) => {
        const toClear = fields.filter((field) => prev[field]);
        if (toClear.length === 0) return prev;
        const next = { ...prev };
        for (const field of toClear) {
          delete next[field];
        }
        return next;
      });
      setSubmitError(null);
    },
    [],
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [membershipRemoving, setMembershipRemoving] = useState<string | null>(
    null
  );
  const [pendingRemoveOrganization, setPendingRemoveOrganization] = useState<
    string | null
  >(null);
  const rolePortalMenuRef = useRef<HTMLDivElement>(null);
  const [roleMenuRect, setRoleMenuRect] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  // The user's *current* project memberships (server truth, snapshot at
  // user-select time) and the working selection the admin is editing. Diffing
  // these two on save yields the add/remove calls.
  const [userProjects, setUserProjects] = useState<string[]>([]);
  const [selectedProjectsEdit, setSelectedProjectsEdit] = useState<string[]>(
    [],
  );

  const usersListScrollRef = useRef<HTMLDivElement>(null);
  const usersListSentinelRef = useRef<HTMLDivElement>(null);
  const editProjectsScrollRef = useRef<HTMLDivElement>(null);
  const editProjectsSentinelRef = useRef<HTMLDivElement>(null);
  const selfProjectsScrollRef = useRef<HTMLDivElement>(null);
  const selfProjectsSentinelRef = useRef<HTMLDivElement>(null);
  const membershipSyncedRef = useRef(false);
  const assignmentSyncGenRef = useRef(0);

  const selectOrganization = useCallback(
    (index: number) => {
      const org = organizations[index];
      if (!org) return;
      clearFieldErrors('organization');
      setFormData((prev) => ({ ...prev, organization: org.name }));
    },
    [organizations, clearFieldErrors],
  );

  const orgMenu = useListboxCombobox({
    itemCount: organizations.length,
    onSelect: selectOrganization,
  });

  const editingPlatformAdminRow = Boolean(
    selectedMembershipRow?.isPlatformAdmin,
  );
  const editingOrgMembershipRow = Boolean(
    selectedMembershipRow && !selectedMembershipRow.isPlatformAdmin,
  );

  const availableEditRoles = useMemo(
    () =>
      currentUser &&
      (currentUser.isPlatformAdmin ||
        canUpdateUserDetails(currentUser.platformRole))
        ? PLATFORM_ROLE_OPTIONS
        : ORG_MEMBERSHIP_ROLE_OPTIONS,
    [currentUser],
  );

  const selectEditRole = useCallback(
    (index: number) => {
      const role = availableEditRoles[index];
      if (!role) return;
      clearFieldErrors('organization');
      setFormData((prev) => ({
        ...prev,
        platformRole: role.value,
        ...(role.value === 'admin'
          ? { organization: '' }
          : selectedMembershipRow && !selectedMembershipRow.isPlatformAdmin
          ? { organization: selectedMembershipRow.organizationName }
          : {}),
      }));
      if (role.value === 'admin' || role.value === 'maintainer') {
        setSelectedProjectsEdit([]);
      }
    },
    [availableEditRoles, selectedMembershipRow, clearFieldErrors],
  );

  const roleMenu = useListboxCombobox({
    itemCount: availableEditRoles.length,
    onSelect: selectEditRole,
    additionalRefs: [rolePortalMenuRef],
  });

  const measureRoleMenuRect = useCallback(() => {
    const el = roleMenu.containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const reservedBottom = 16;
    const spaceBelow = window.innerHeight - rect.bottom - gap - reservedBottom;
    const maxHeight = Math.max(120, Math.min(360, spaceBelow));
    setRoleMenuRect({
      top: rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      maxHeight,
    });
  }, [roleMenu.containerRef]);

  useLayoutEffect(() => {
    if (!roleMenu.isOpen) {
      setRoleMenuRect(null);
      return;
    }
    measureRoleMenuRect();
    window.addEventListener('resize', measureRoleMenuRect);
    window.addEventListener('scroll', measureRoleMenuRect, true);
    return () => {
      window.removeEventListener('resize', measureRoleMenuRect);
      window.removeEventListener('scroll', measureRoleMenuRect, true);
    };
  }, [roleMenu.isOpen, measureRoleMenuRect]);

  const selectedEditRole = availableEditRoles.find(
    (role) => role.value === formData.platformRole,
  );

  // Three modes:
  //   - Admin: global access, all fields editable.
  //   - Maintainer: picker filtered to active org; can edit contact info,
  //     org role (Maintainer/Developer), and project memberships.
  //   - Developer (self-edit only): skip the picker, only last_name + phone.
  const isAdmin = Boolean(
    currentUser &&
      (currentUser.isPlatformAdmin ||
        canUpdateUserDetails(currentUser.platformRole))
  );
  const canEditOthers = Boolean(
    currentUser && canEditOrgUsers(currentUser.platformRole),
  );
  const isMaintainer =
    Boolean(currentUser) && canEditOthers && !isAdmin;
  const isSelfEdit = Boolean(currentUser) && !canEditOthers;

  const debouncedUserSearch = useDebouncedValue(searchQuery, 300);
  const maintainerOrgId = isMaintainer
    ? currentUser?.activeOrganizationId ?? getSessionActiveOrganizationId()
    : undefined;

  const fetchUsersPage = useCallback(
    async ({
      pageNumber,
      pageSize,
      search,
      signal,
    }: {
      pageNumber: number;
      pageSize: number;
      search?: string;
      signal?: AbortSignal;
    }) => {
      const response = await listUsers({
        pageNumber,
        pageSize,
        search,
        signal,
        organizationId:
          isMaintainer && maintainerOrgId != null ? maintainerOrgId : undefined,
      });
      return {
        items: (response.users ?? []).map(userFromBackend),
        page_number: response.page_number,
        page_size: response.page_size,
        total_count: response.total_count,
        has_more: response.has_more,
      };
    },
    [isMaintainer, maintainerOrgId]
  );

  const {
    items: loadedUsers,
    isLoading: usersLoading,
    isLoadingMore: usersLoadingMore,
    hasMore: usersHasMore,
    loadMore: loadMoreUsers,
  } = usePaginatedList<User>({
    pageSize: DEFAULT_LIST_PAGE_SIZE,
    fetchPage: fetchUsersPage,
    search: debouncedUserSearch,
    enabled: !isLoading && !isSelfEdit && currentStep === 1,
    getItemKey: (user) => user.id,
  });

  const fetchSelfProjectsPage = useCallback(
    async ({
      pageNumber,
      pageSize,
      signal,
    }: {
      pageNumber: number;
      pageSize: number;
      signal?: AbortSignal;
    }) => {
      const response = await listMyProjects({
        pageNumber,
        pageSize,
        signal,
      });
      return {
        items: response.projects ?? [],
        page_number: response.page_number,
        page_size: response.page_size,
        total_count: response.total_count,
        has_more: response.has_more,
      };
    },
    []
  );

  const {
    items: selfProjectRows,
    isLoading: selfProjectsLoading,
    isLoadingMore: selfProjectsLoadingMore,
    hasMore: selfProjectsHasMore,
    loadMore: loadMoreSelfProjects,
    error: selfProjectsLoadError,
  } = usePaginatedList({
    pageSize: DEFAULT_LIST_PAGE_SIZE,
    fetchPage: fetchSelfProjectsPage,
    enabled: isSelfEdit && Boolean(currentUser),
    getItemKey: (row) => row.project_id,
  });

  const selfProjects = useMemo(
    () =>
      selfProjectRows.map((row) => ({
        id: String(row.project_id),
        name: row.project_name,
        customer: row.organization_name,
      })),
    [selfProjectRows]
  );

  useEffect(() => {
    void ensureOrganizations();
  }, [ensureOrganizations]);

  const editingOwnMembership = Boolean(
    currentUser && selectedUser && selectedUser.id === currentUser.id,
  );
  const selectedIsPlatformAdmin = Boolean(
    selectedMembershipRow?.isPlatformAdmin ||
      (selectedUser && isPlatformAdminUser(selectedUser)),
  );
  const editingAdminRole = formData.platformRole === 'admin';
  const demotingFromAdmin = Boolean(
    isAdmin &&
      selectedMembershipRow?.isPlatformAdmin &&
      formData.platformRole !== 'admin',
  );
  const promotingToAdmin = Boolean(
    isAdmin && editingOrgMembershipRow && formData.platformRole === 'admin',
  );
  const showOrgRoleGrid = Boolean(
    isSelfEdit ||
      (editingOrgMembershipRow && !demotingFromAdmin && !promotingToAdmin),
  );
  const canRemoveOrgMembership = Boolean(
    !isSelfEdit &&
      editingOrgMembershipRow &&
      !editingOwnMembership &&
      !editingAdminRole,
  );
  const membershipOrganizationName =
    selectedMembershipRow?.organizationName?.trim() ?? '';
  const roleFieldLocked = Boolean(isSelfEdit || editingOwnMembership);
  const editingMaintainer = formData.platformRole === 'maintainer';
  const resolvedEditOrganization = useMemo(() => {
    if (editingAdminRole && !demotingFromAdmin) {
      return '';
    }
    const fromForm = formData.organization.trim();
    if (fromForm) return fromForm;
    if (selectedMembershipRow?.isPlatformAdmin || demotingFromAdmin) {
      return '';
    }
    return (
      membershipOrganizationName ||
      selectedUser?.organization?.trim() ||
      ''
    );
  }, [
    editingAdminRole,
    demotingFromAdmin,
    formData.organization,
    selectedMembershipRow?.isPlatformAdmin,
    membershipOrganizationName,
    selectedUser?.organization,
  ]);
  const hasResolvedEditOrganization = Boolean(resolvedEditOrganization);

  const resolvedEditOrgId = useMemo(() => {
    if (!resolvedEditOrganization) return undefined;
    const orgMatch = organizations.find(
      (org) =>
        org.name.trim().toLowerCase() ===
        resolvedEditOrganization.trim().toLowerCase()
    );
    const orgId = orgMatch ? Number(orgMatch.id) : undefined;
    return orgId != null && !Number.isNaN(orgId) && orgId > 0 ? orgId : undefined;
  }, [resolvedEditOrganization, organizations]);

  const fetchEditProjectsPage = useCallback(
    async ({
      pageNumber,
      pageSize,
      signal,
      organizationId,
    }: {
      pageNumber: number;
      pageSize: number;
      signal?: AbortSignal;
      organizationId?: number;
    }) => {
      const orgId =
        typeof organizationId === 'number' ? organizationId : undefined;
      if (orgId == null || orgId <= 0) {
        return {
          items: [],
          page_number: 0,
          page_size: pageSize,
          total_count: 0,
          has_more: false,
        };
      }
      const response = await listProjects({
        pageNumber,
        pageSize,
        organizationId: orgId,
        includeAssignees: true,
        signal,
      });
      return {
        items: response.projects ?? [],
        page_number: response.page_number,
        page_size: response.page_size,
        total_count: response.total_count,
        has_more: response.has_more,
      };
    },
    []
  );

  const {
    items: editProjectDtos,
    isLoading: projectsLoading,
    isLoadingMore: projectsLoadingMore,
    hasMore: projectsHasMore,
    totalCount: editProjectsTotalCount,
    error: projectsLoadError,
    loadMore: loadMoreEditProjects,
  } = usePaginatedList<BackendProjectDTO>({
    pageSize: DEFAULT_LIST_PAGE_SIZE,
    fetchPage: fetchEditProjectsPage,
    filters: { organizationId: resolvedEditOrgId },
    enabled:
      Boolean(selectedUser && !isSelfEdit && resolvedEditOrgId) &&
      !editingMaintainer &&
      !editingAdminRole,
    getItemKey: (item) => item.project_id,
  });

  const orgProjectsForEdit = useMemo((): Project[] => {
    if (!resolvedEditOrganization) return [];
    const orgKey = resolvedEditOrganization.trim().toLowerCase();
    return editProjectDtos
      .filter(
        (dto) => dto.organization_name.trim().toLowerCase() === orgKey
      )
      .map((dto) => projectFromBackend(dto));
  }, [editProjectDtos, resolvedEditOrganization]);

  const orgRoleGridHint = useMemo(() => {
    if (roleFieldLocked) return '';
    const org = resolvedEditOrganization || formData.organization;
    if (!org) return '';
    if (canRemoveOrgMembership) {
      return `Role applies only in ${org}. Removing membership also revokes project access in ${org}.`;
    }
    if (editingOrgMembershipRow) {
      return `Role applies only in ${org}. To edit another organization, go back and choose that row.`;
    }
    return '';
  }, [
    resolvedEditOrganization,
    formData.organization,
    roleFieldLocked,
    canRemoveOrgMembership,
    editingOrgMembershipRow,
  ]);

  const filteredMembershipRows = useMemo(() => {
    const maintainerWorkspaceOrgId = isMaintainer
      ? currentUser?.activeOrganizationId ?? getSessionActiveOrganizationId()
      : undefined;
    return expandUsersToMembershipRows(loadedUsers, {
      // When workspace is not resolved yet, restrict to an impossible id so we
      // do not briefly expose cross-org rows from the cached users list.
      restrictToOrganizationId: isMaintainer
        ? maintainerWorkspaceOrgId ?? -1
        : undefined,
    });
  }, [loadedUsers, isMaintainer, currentUser?.activeOrganizationId]);
  const initialMembershipRole = selectedMembershipRow?.orgRole;
  const steps = isSelfEdit ? SELF_EDIT_STEPS : EDIT_USER_STEPS;

  const applyMembershipRowToForm = useCallback(
    (row: UserMembershipRow) => {
      const user = loadedUsers.find((item) => item.id === row.userId);
      if (!user) return false;
      const isSameMembership =
        selectedMembershipRow?.rowKey === row.rowKey &&
        selectedUser?.id === user.id;
      setSelectedMembershipRow(row);
      setSelectedUser(user);
      setFormData({
        userId: user.id,
        organization: row.organizationName,
        platformRole: row.isPlatformAdmin ? 'admin' : row.orgRole,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || '',
      });
      if (!isSameMembership) {
        setUserProjects([]);
        setSelectedProjectsEdit([]);
        membershipSyncedRef.current = false;
      }
      return true;
    },
    [loadedUsers, selectedMembershipRow?.rowKey, selectedUser?.id],
  );

  // Auto-select the current user in self-edit mode
  // on the Edit step. Guarded on `selectedUser` so we don't clobber state
  // after the admin (incorrectly) flips modes mid-session.
  useEffect(() => {
    if (!isSelfEdit || !currentUser || selectedUser) return;
    setSelectedUser(currentUser);
    setFormData({
      userId: currentUser.id,
      organization: currentUser.organization,
      platformRole: currentUser.platformRole,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      phone: currentUser.phone || '',
    });
  }, [isSelfEdit, currentUser, selectedUser]);

  useEffect(() => {
    if (!selectedMembershipRow) return;
    const refreshed = filteredMembershipRows.find(
      (row) => row.rowKey === selectedMembershipRow.rowKey,
    );
    if (refreshed) setSelectedMembershipRow(refreshed);
  }, [filteredMembershipRows, selectedMembershipRow]);

  useEffect(() => {
    if (!selectedUser?.id) return;
    const refreshed = loadedUsers.find((user) => user.id === selectedUser.id);
    if (refreshed) setSelectedUser(refreshed);
  }, [loadedUsers, selectedUser]);

  useEffect(() => {
    if (
      !selectedUser ||
      isSelfEdit ||
      !resolvedEditOrgId ||
      editingMaintainer ||
      editingAdminRole
    ) {
      setUserProjects([]);
      setSelectedProjectsEdit([]);
      membershipSyncedRef.current = false;
      return;
    }

    const userId = Number(selectedUser.id);
    if (!Number.isFinite(userId) || userId <= 0) return;

    membershipSyncedRef.current = false;
    const generation = ++assignmentSyncGenRef.current;
    const controller = new AbortController();

    void (async () => {
      try {
        const assignedIds = await listUserAssignedProjectIds({
          organizationId: resolvedEditOrgId,
          userId,
          signal: controller.signal,
        });
        if (controller.signal.aborted || generation !== assignmentSyncGenRef.current) {
          return;
        }
        if (membershipSyncedRef.current) return;
        setUserProjects(assignedIds);
        setSelectedProjectsEdit(assignedIds);
        membershipSyncedRef.current = true;
      } catch {
        if (controller.signal.aborted || generation !== assignmentSyncGenRef.current) {
          return;
        }
      }
    })();

    return () => controller.abort();
  }, [
    selectedUser?.id,
    resolvedEditOrgId,
    isSelfEdit,
    editingMaintainer,
    editingAdminRole,
  ]);

  useScrollPaginatedLoadMore({
    scrollContainerRef: usersListScrollRef,
    sentinelRef: usersListSentinelRef,
    hasMore: usersHasMore,
    isLoading: usersLoading,
    isLoadingMore: usersLoadingMore,
    onLoadMore: loadMoreUsers,
    enabled: !isSelfEdit && currentStep === 1,
    itemCount: filteredMembershipRows.length,
  });

  useScrollPaginatedLoadMore({
    scrollContainerRef: editProjectsScrollRef,
    sentinelRef: editProjectsSentinelRef,
    hasMore: projectsHasMore,
    isLoading: projectsLoading,
    isLoadingMore: projectsLoadingMore,
    onLoadMore: loadMoreEditProjects,
    enabled:
      Boolean(selectedUser && !isSelfEdit && resolvedEditOrgId) &&
      currentStep === 2 &&
      !editingMaintainer &&
      !editingAdminRole,
    itemCount: orgProjectsForEdit.length,
  });

  useScrollPaginatedLoadMore({
    scrollContainerRef: selfProjectsScrollRef,
    sentinelRef: selfProjectsSentinelRef,
    hasMore: selfProjectsHasMore,
    isLoading: selfProjectsLoading,
    isLoadingMore: selfProjectsLoadingMore,
    onLoadMore: loadMoreSelfProjects,
    enabled: isSelfEdit && Boolean(currentUser),
    itemCount: selfProjectRows.length,
  });

  const toggleProjectAssignmentEdit = useCallback((projectId: string) => {
    membershipSyncedRef.current = true;
    const normalizedId = normalizeProjectId(projectId);
    setSelectedProjectsEdit((prev) =>
      prev.some((id) => normalizeProjectId(id) === normalizedId)
        ? prev.filter((id) => normalizeProjectId(id) !== normalizedId)
        : [...prev, normalizedId],
    );
  }, []);

  /** Projects in the user's org the admin/maintainer can toggle, plus any current memberships (covers org-name drift and partial project cache). */
  const assignableProjects = useMemo((): Project[] => {
    if (!selectedUser) return [];
    const targetOrg = resolvedEditOrganization;
    if (!targetOrg) return [];
    const inOrg = orgProjectsForEdit;
    const seen = new Set(inOrg.map((p) => p.id));
    const memberExtras = userProjects
      .map((id) => orgProjectsForEdit.find((p) => p.id === id))
      .filter(
        (proj): proj is Project =>
          proj !== undefined && !seen.has(proj.id),
      );
    return [...inOrg, ...memberExtras];
  }, [selectedUser, resolvedEditOrganization, orgProjectsForEdit, userProjects]);

  const pageTitle = isSelfEdit ? 'Edit My Profile' : 'Edit User Details';

  if (isLoading && !currentUser) {
    return (
      <>
        <OnboardingSubHeader title={pageTitle} showBackButton />
        <main className="flex flex-1 flex-col bg-surface-muted">
          <LoadingIndicator layout="page" />
        </main>
      </>
    );
  }

  const handleSelectMembershipRow = (row: UserMembershipRow) => {
    clearFieldErrors('userId');
    applyMembershipRowToForm(row);
  };

  const validateStep1 = (): boolean => {
    if (!selectedMembershipRow || !selectedUser) {
      setErrors({ userId: 'Please select a membership to edit' });
      return false;
    }
    setErrors({});
    return true;
  };

  const validateStep2 = (): boolean => {
    const newErrors: EditUserFormErrors = {};

    if (demotingFromAdmin && !formData.organization.trim()) {
      newErrors.organization =
        'Select the organization this user will belong to after removing Admin access';
    }

    const firstNameError = validatePersonName(formData.firstName);
    if (firstNameError) {
      newErrors.firstName = firstNameError;
    }
    const lastNameError = validatePersonName(formData.lastName);
    if (lastNameError) {
      newErrors.lastName = lastNameError;
    }
    const phoneError = validatePhoneNumber(formData.phone);
    if (phoneError) {
      newErrors.phone = phoneError;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Map the linear step counter to the logical phase. In admin mode steps
  // 1/2/3 mean pick/edit/review; in self-edit mode 1/2 mean edit/review
  // (the picker is skipped because the user can only edit themselves).
  const showPicker = !isSelfEdit && currentStep === 1;
  const showEdit =
    (isSelfEdit && currentStep === 1) || (!isSelfEdit && currentStep === 2);
  const showReview =
    (isSelfEdit && currentStep === 2) || (!isSelfEdit && currentStep === 3);

  const handleNext = () => {
    if (showPicker && !validateStep1()) return;
    if (showPicker && selectedMembershipRow) {
      const freshRow =
        filteredMembershipRows.find(
          (row) => row.rowKey === selectedMembershipRow.rowKey,
        ) ?? selectedMembershipRow;
      applyMembershipRowToForm(freshRow);
    }
    if (showEdit && !validateStep2()) return;
    setSubmitError(null);
    setCurrentStep((prev) => Math.min(prev + 1, steps.length));
  };

  const handleBack = () => {
    setErrors({});
    setSubmitError(null);
    if (!isSelfEdit && currentStep === 2 && selectedMembershipRow) {
      const freshRow =
        filteredMembershipRows.find(
          (row) => row.rowKey === selectedMembershipRow.rowKey,
        ) ?? selectedMembershipRow;
      applyMembershipRowToForm(freshRow);
    }
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!selectedUser || submitting) return;
    if (!isSelfEdit && !selectedMembershipRow) return;
    if (!validateStep2()) {
      if (!isSelfEdit) {
        setCurrentStep(2);
      }
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const patch: {
        organization?: string;
        platformRole?: typeof formData.platformRole;
        firstName?: string;
        lastName?: string;
        phone?: string;
      } = {};
      const demotingPlatformAdmin = demotingFromAdmin;
      const roleChanged =
        initialMembershipRole != null &&
        formData.platformRole !== initialMembershipRole;

      if (demotingPlatformAdmin && !editingOwnMembership) {
        patch.organization = formData.organization;
        patch.platformRole = formData.platformRole;
      } else if (
        editingOrgMembershipRow &&
        roleChanged &&
        !editingOwnMembership
      ) {
        if (isAdmin && formData.platformRole !== 'admin') {
          patch.organization =
            formData.organization || membershipOrganizationName;
        }
        patch.platformRole = formData.platformRole;
      } else if (
        editingPlatformAdminRow &&
        roleChanged &&
        formData.platformRole === 'admin' &&
        !editingOwnMembership
      ) {
        patch.platformRole = formData.platformRole;
      }
      if (formData.firstName !== selectedUser.firstName) {
        patch.firstName = formData.firstName;
      }
      if (formData.lastName !== selectedUser.lastName) {
        patch.lastName = formData.lastName;
      }
      if (formData.phone !== (selectedUser.phone || '')) {
        patch.phone = formData.phone;
      }
      if (Object.keys(patch).length > 0) {
        await updateUser(selectedUser.id, patch);
      }

      const failedProjectChanges: string[] = [];
      for (const projectId of projectsToAdd) {
        try {
          await updateProject(projectId, {
            addUserEmail: selectedUser.email,
          });
        } catch (assignErr) {
          const project = assignableProjects.find((p) => p.id === projectId);
          failedProjectChanges.push(
            `add ${project?.name ?? `project ${projectId}`}`,
          );
          // eslint-disable-next-line no-console
          console.error(
            `Failed to add ${selectedUser.email} to project ${projectId}`,
            assignErr,
          );
        }
      }
      for (const projectId of projectsToRemove) {
        try {
          await updateProject(projectId, {
            removeUserId: selectedUser.id,
          });
        } catch (assignErr) {
          const project = assignableProjects.find((p) => p.id === projectId);
          failedProjectChanges.push(
            `remove ${project?.name ?? `project ${projectId}`}`,
          );
          // eslint-disable-next-line no-console
          console.error(
            `Failed to remove ${selectedUser.email} from project ${projectId}`,
            assignErr,
          );
        }
      }

      if (failedProjectChanges.length > 0) {
        setSubmitError(
          `User updated, but the following project changes failed: ${failedProjectChanges.join(
            ', ',
          )}.`,
        );
        setSubmitting(false);
        return;
      }

      navigate(`${pageRoutes.onboarding}?success=user-edit`);
    } catch (err: any) {
      const message =
        err?.displayMessage ?? 'Failed to update user. Please try again.';
      if (String(message).toLowerCase().includes('phone')) {
        setErrors({ phone: message });
        setCurrentStep(isSelfEdit ? 1 : 2);
        setSubmitting(false);
        return;
      }
      setSubmitError(message);
      setSubmitting(false);
    }
  };

  const handleRemoveMembership = async (organizationName: string) => {
    if (!selectedUser || membershipRemoving) return;
    setMembershipRemoving(organizationName);
    setSubmitError(null);
    try {
      await removeOrgUser({
        email: selectedUser.email,
        organization: organizationName,
      });
      setPendingRemoveOrganization(null);
      navigate(
        `${pageRoutes.onboarding}?success=user-membership-removed&org=${encodeURIComponent(organizationName)}`,
      );
    } catch (err: any) {
      setSubmitError(
        err?.displayMessage ??
          'Failed to remove organization membership.'
      );
    } finally {
      setMembershipRemoving(null);
    }
  };

  const projectsToAdd =
    editingMaintainer || editingAdminRole
      ? []
      : selectedProjectsEdit.filter(
          (id) =>
            !userProjects.some(
              (existingId) =>
                normalizeProjectId(existingId) === normalizeProjectId(id),
            ),
        );
  const projectsToRemove =
    editingMaintainer || editingAdminRole
      ? []
      : userProjects.filter(
          (id) =>
            !selectedProjectsEdit.some(
              (selectedId) =>
                normalizeProjectId(selectedId) === normalizeProjectId(id),
            ),
        );
  const hasProjectChanges =
    projectsToAdd.length > 0 || projectsToRemove.length > 0;

  const hasChanges = Boolean(
    selectedUser &&
      (isSelfEdit
        ? formData.firstName !== selectedUser.firstName ||
          formData.lastName !== selectedUser.lastName ||
          formData.phone !== (selectedUser.phone || '')
        : selectedMembershipRow &&
          ((!editingOwnMembership &&
            initialMembershipRole != null &&
            formData.platformRole !== initialMembershipRole) ||
            formData.firstName !== selectedUser.firstName ||
            formData.lastName !== selectedUser.lastName ||
            formData.phone !== (selectedUser.phone || '') ||
            hasProjectChanges)),
  );

  return (
    <>
      <OnboardingSubHeader title={pageTitle} showBackButton />
      <OnboardingStepper steps={steps} currentStep={currentStep} />

      <main className="bg-surface-muted px-8 py-8">
        <div className="max-w-3xl mx-auto">
          {showPicker && (
            <OnboardingSurface className="p-8">
              <h2
                className="text-xl font-bold text-brand mb-2 uppercase"
                aria-label="Step 1: Select User"
              >
                Step 1: Select User
              </h2>
              <p className="text-gray-600 text-sm mb-8">
                {isMaintainer && currentUser
                  ? `Choose a membership in ${currentUser.organization} to update contact info, role, or project assignments.`
                  : 'Each row is one organization membership. Select the row you want to edit.'}
              </p>
              <ActiveOrganizationNotice className="mb-6" />

              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search users by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {errors.userId && (
                  <p className="text-sm text-red-500">{errors.userId}</p>
                )}

                <OnboardingNestedPanel
                  ref={usersListScrollRef}
                  className="max-h-96 divide-y divide-gray-200 overflow-y-auto"
                >
                  {usersLoading && filteredMembershipRows.length === 0 ? (
                    <div className="flex justify-center px-4 py-8">
                      <LoadingSpinner size="lg" className="text-brand" />
                    </div>
                  ) : filteredMembershipRows.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500">
                      No memberships found matching your search.
                    </div>
                  ) : (
                    filteredMembershipRows.map((row) => {
                      const isSelected =
                        selectedMembershipRow?.rowKey === row.rowKey;
                      const isCurrentUser =
                        currentUser != null && row.userId === currentUser.id;
                      return (
                        <button
                          key={row.rowKey}
                          type="button"
                          onClick={() => handleSelectMembershipRow(row)}
                          className={cn(
                            'w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors',
                            isSelected && 'bg-edit-muted'
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-brand rounded-full flex items-center justify-center text-white text-sm font-medium">
                              {getUserInitials(row.firstName, row.lastName)}
                            </div>
                            <div className="text-left">
                              <p className="font-medium text-gray-900">
                                {formatUserDisplayName(
                                  row.firstName,
                                  row.lastName
                                )}
                                {isCurrentUser && (
                                  <span className="ml-1.5 text-sm font-normal text-gray-500">
                                    (You)
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-gray-500">
                                {row.email}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right text-sm">
                              {isAdmin && !row.isPlatformAdmin && (
                                <p
                                  className="max-w-[12rem] truncate text-gray-600"
                                  title={row.organizationName}
                                >
                                  {row.organizationName}
                                </p>
                              )}
                              {isAdmin && row.isPlatformAdmin && (
                                <p className="text-gray-600">Global</p>
                              )}
                              <span
                                className={cn(
                                  'px-2 py-0.5 rounded text-xs font-medium',
                                  row.orgRole === 'admin'
                                    ? 'bg-red-100 text-red-700'
                                    : row.orgRole === 'maintainer'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-gray-100 text-gray-700'
                                )}
                              >
                                {formatPlatformRoleLabel(row.orgRole)}
                              </span>
                            </div>
                            <div
                              className={cn(
                                'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                                isSelected
                                  ? 'bg-edit border-edit'
                                  : 'border-gray-300'
                              )}
                            >
                              {isSelected && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                  <div ref={usersListSentinelRef} className="flex justify-center px-4 py-3">
                    {usersLoadingMore && (
                      <LoadingSpinner size="sm" className="text-brand" />
                    )}
                  </div>
                </OnboardingNestedPanel>
              </div>

              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => navigate(pageRoutes.onboarding)}
                >
                  Cancel
                </Button>
                <Button onClick={handleNext}>Continue</Button>
              </div>
            </OnboardingSurface>
          )}

          {showEdit && selectedUser && (
            <OnboardingSurface className="p-8">
              <h2
                className="text-xl font-bold text-brand mb-2 uppercase"
                aria-label={
                  isSelfEdit
                    ? 'Step 1: Edit Profile'
                    : 'Step 2: Edit Details'
                }
              >
                {isSelfEdit ? 'Step 1: Edit Profile' : 'Step 2: Edit Details'}
              </h2>
              <p className="text-gray-600 text-sm mb-4">
                {isSelfEdit || editingOwnMembership
                  ? 'Update your contact information. Organization and role cannot be changed on your own account.'
                  : isMaintainer
                  ? `Update contact information, role, and project memberships for ${formatUserDisplayName(selectedUser.firstName, selectedUser.lastName)}.`
                  : `Update the details for ${formatUserDisplayName(selectedUser.firstName, selectedUser.lastName)}.`}
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-brand rounded-full flex items-center justify-center text-white font-medium shrink-0">
                  {getUserInitials(formData.firstName, formData.lastName)}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 break-words">
                    {formatUserDisplayName(
                      formData.firstName,
                      formData.lastName
                    )}
                  </p>
                  <p className="text-sm text-gray-500 break-all">
                    {selectedUser.email}
                  </p>
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-8">
                <p className="text-xs text-yellow-800">
                  <Lock className="w-3 h-3 inline mr-1" />
                  {editingOwnMembership ? (
                    <>
                      <span className="font-medium">Email</span>,{' '}
                      <span className="font-medium">Organization</span>, and{' '}
                      <span className="font-medium">Role</span> cannot be
                      changed on your own account.
                    </>
                  ) : canEditOthers ? (
                    <>
                      <span className="font-medium">Email</span> cannot be
                      changed after account creation.
                    </>
                  ) : (
                    <>
                      <span className="font-medium">Email</span>,{' '}
                      <span className="font-medium">Organization</span>, and{' '}
                      <span className="font-medium">Role</span> are managed by
                      an Admin and cannot be changed here.
                    </>
                  )}
                </p>
              </div>

              <div className="space-y-6">
                {showOrgRoleGrid && (isAdmin || isMaintainer || isSelfEdit) && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 items-start">
                      <div className="space-y-2 min-w-0">
                        <Label className="flex items-center gap-1.5">
                          <Lock className="w-3 h-3" />
                          Organization
                          <span className="text-red-500"> *</span>
                        </Label>
                        <Input
                          value={
                            resolvedEditOrganization || formData.organization
                          }
                          disabled
                          className="bg-gray-100 text-gray-500 cursor-not-allowed"
                        />
                      </div>
                      <div className="space-y-2 min-w-0">
                        <Label
                          className={cn(
                            'flex items-center gap-1.5',
                            roleFieldLocked && 'text-gray-700',
                          )}
                        >
                          {roleFieldLocked ? (
                            <Lock className="w-3 h-3" />
                          ) : (
                            <Pencil className="w-3 h-3 text-edit-light" />
                          )}
                          Role {!roleFieldLocked && (
                            <span className="text-red-500">*</span>
                          )}
                        </Label>
                        {roleFieldLocked ? (
                          <Input
                            value={formatPlatformRoleLabel(
                              initialMembershipRole ??
                                selectedUser.platformRole,
                            )}
                            disabled
                            className="bg-gray-100 text-gray-500 cursor-not-allowed"
                          />
                        ) : (
                          <div className="relative" ref={roleMenu.containerRef}>
                            <button
                              type="button"
                              onClick={roleMenu.toggle}
                              onKeyDown={roleMenu.onKeyDown}
                              aria-haspopup="listbox"
                              aria-expanded={roleMenu.isOpen}
                              aria-controls={roleMenu.menuId}
                              className={cn(
                                EDIT_COMBO_TRIGGER_CLASS,
                                'border-gray-200',
                              )}
                            >
                              <span className="text-gray-900">
                                {selectedEditRole
                                  ? selectedEditRole.label
                                  : 'Select role'}
                              </span>
                              <ChevronDown
                                className={cn(
                                  'w-4 h-4 text-gray-500 transition-transform',
                                  roleMenu.isOpen && 'rotate-180',
                                )}
                              />
                            </button>
                            {roleMenu.isOpen &&
                              roleMenuRect &&
                              createPortal(
                                <div
                                  ref={rolePortalMenuRef}
                                  id={roleMenu.menuId}
                                  role="listbox"
                                  className="border border-gray-200 bg-white"
                                  style={{
                                    position: 'fixed',
                                    top: roleMenuRect.top,
                                    left: roleMenuRect.left,
                                    width: roleMenuRect.width,
                                    maxHeight: roleMenuRect.maxHeight,
                                    overflowY: 'auto',
                                    WebkitOverflowScrolling: 'touch',
                                    zIndex: 2147483646,
                                    borderRadius: 6,
                                    boxShadow:
                                      '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                                  }}
                                >
                                  {availableEditRoles.map((role, index) => (
                                    <button
                                      key={role.value}
                                      type="button"
                                      role="option"
                                      id={roleMenu.getOptionId(index)}
                                      aria-selected={
                                        formData.platformRole === role.value
                                      }
                                      onClick={() => roleMenu.select(index)}
                                      onMouseEnter={() =>
                                        roleMenu.setActiveIndex(index)
                                      }
                                      className={cn(
                                        'w-full px-3 py-2.5 text-left text-sm',
                                        formData.platformRole === role.value &&
                                          'bg-edit-muted',
                                        roleMenu.activeIndex === index
                                          ? 'bg-gray-100'
                                          : 'hover:bg-gray-50',
                                      )}
                                    >
                                      <RoleMenuOption
                                        role={role}
                                        selected={
                                          formData.platformRole === role.value
                                        }
                                        checkClassName="text-edit-light"
                                      />
                                    </button>
                                  ))}
                                </div>,
                                document.body,
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                    {(orgRoleGridHint || canRemoveOrgMembership) && (
                      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                        <p className="text-xs text-gray-600 min-w-0 flex-1">
                          {orgRoleGridHint}
                        </p>
                        {canRemoveOrgMembership && membershipOrganizationName && (
                          <Button
                            type="button"
                            variant="outline"
                            disabled={
                              membershipRemoving === membershipOrganizationName
                            }
                            onClick={() =>
                              setPendingRemoveOrganization(
                                membershipOrganizationName,
                              )
                            }
                            className="shrink-0 h-8 px-3 text-xs text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800"
                          >
                            {membershipRemoving === membershipOrganizationName
                              ? 'Removing…'
                              : `Remove from ${membershipOrganizationName}`}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {isAdmin && (
                <>
                {demotingFromAdmin && !editingOwnMembership && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <Pencil className="w-3 h-3 text-edit-light" />
                    Organization <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500">
                    Required when removing Admin access
                  </p>
                  <div className="relative" ref={orgMenu.containerRef}>
                    <button
                      type="button"
                      onClick={orgMenu.toggle}
                      onKeyDown={orgMenu.onKeyDown}
                      aria-haspopup="listbox"
                      aria-expanded={orgMenu.isOpen}
                      aria-controls={orgMenu.menuId}
                      className={cn(
                        EDIT_COMBO_TRIGGER_CLASS,
                        errors.organization
                          ? 'border-red-500'
                          : 'border-gray-200',
                      )}
                    >
                      <span
                        className={
                          formData.organization
                            ? 'text-gray-900'
                            : 'text-gray-500'
                        }
                      >
                        {formData.organization || 'Select organization'}
                      </span>
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    </button>
                    {orgMenu.isOpen && (
                      <div
                        id={orgMenu.menuId}
                        role="listbox"
                        className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg"
                      >
                        {organizations.map((org, index) => (
                          <button
                            key={org.id}
                            type="button"
                            role="option"
                            id={orgMenu.getOptionId(index)}
                            aria-selected={formData.organization === org.name}
                            onClick={() => orgMenu.select(index)}
                            onMouseEnter={() => orgMenu.setActiveIndex(index)}
                            className={cn(
                              'w-full px-3 py-2 text-left text-sm',
                              formData.organization === org.name &&
                                'bg-edit-muted',
                              orgMenu.activeIndex === index
                                ? 'bg-gray-100'
                                : 'hover:bg-gray-50'
                            )}
                          >
                            {org.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {errors.organization && (
                    <p className="text-sm text-red-500">{errors.organization}</p>
                  )}
                </div>
                )}

                {!showOrgRoleGrid && (
                <div className="space-y-2">
                  <Label
                    className={cn(
                      'flex items-center gap-1.5',
                      editingOwnMembership && 'text-gray-700',
                    )}
                  >
                    {editingOwnMembership ? (
                      <Lock className="w-3 h-3" />
                    ) : (
                      <Pencil className="w-3 h-3 text-edit-light" />
                    )}
                    Role {!editingOwnMembership && (
                      <span className="text-red-500">*</span>
                    )}
                  </Label>
                  <p className="text-xs text-gray-500">
                    {editingOwnMembership
                      ? 'Another Admin or Maintainer must change your role.'
                      : editingPlatformAdminRow || promotingToAdmin
                      ? 'Admin has global access. Maintainer and Developer are scoped to an organization.'
                      : 'Role applies only in the organization above.'}
                  </p>
                  {promotingToAdmin && !editingOwnMembership && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      Granting Admin access is global. Organization membership
                      and project assignments for{' '}
                      <span className="font-medium">
                        {membershipOrganizationName}
                      </span>{' '}
                      will no longer apply once Admin access is saved.
                    </p>
                  )}
                  {demotingFromAdmin && !editingOwnMembership && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      Removing Admin access requires an organization above. The
                      user will become a{' '}
                      {formatPlatformRoleLabel(formData.platformRole)} in that
                      org.
                    </p>
                  )}
                  {editingOwnMembership ? (
                    <Input
                      value={formatPlatformRoleLabel(
                        initialMembershipRole ?? selectedUser.platformRole,
                      )}
                      disabled
                      className="bg-gray-100 text-gray-500 cursor-not-allowed"
                    />
                  ) : (
                    <div className="relative" ref={roleMenu.containerRef}>
                      <button
                        type="button"
                        onClick={roleMenu.toggle}
                        onKeyDown={roleMenu.onKeyDown}
                        aria-haspopup="listbox"
                        aria-expanded={roleMenu.isOpen}
                        aria-controls={roleMenu.menuId}
                        className={cn(
                          EDIT_COMBO_TRIGGER_CLASS,
                          'border-gray-200',
                        )}
                      >
                        <span className="text-gray-900">
                          {selectedEditRole
                            ? selectedEditRole.label
                            : 'Select role'}
                        </span>
                        <ChevronDown
                          className={cn(
                            'w-4 h-4 text-gray-500 transition-transform',
                            roleMenu.isOpen && 'rotate-180'
                          )}
                        />
                      </button>
                      {roleMenu.isOpen &&
                        roleMenuRect &&
                        createPortal(
                          <div
                            ref={rolePortalMenuRef}
                            id={roleMenu.menuId}
                            role="listbox"
                            className="border border-gray-200 bg-white"
                            style={{
                              position: 'fixed',
                              top: roleMenuRect.top,
                              left: roleMenuRect.left,
                              width: roleMenuRect.width,
                              maxHeight: roleMenuRect.maxHeight,
                              overflowY: 'auto',
                              WebkitOverflowScrolling: 'touch',
                              zIndex: 2147483646,
                              borderRadius: 6,
                              boxShadow:
                                '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0.05)',
                            }}
                          >
                            {availableEditRoles.map((role, index) => (
                              <button
                                key={role.value}
                                type="button"
                                role="option"
                                id={roleMenu.getOptionId(index)}
                                aria-selected={
                                  formData.platformRole === role.value
                                }
                                onClick={() => roleMenu.select(index)}
                                onMouseEnter={() =>
                                  roleMenu.setActiveIndex(index)
                                }
                                className={cn(
                                  'w-full px-3 py-2.5 text-left text-sm',
                                  formData.platformRole === role.value &&
                                    'bg-edit-muted',
                                  roleMenu.activeIndex === index
                                    ? 'bg-gray-100'
                                    : 'hover:bg-gray-50'
                                )}
                              >
                                <RoleMenuOption
                                  role={role}
                                  selected={
                                    formData.platformRole === role.value
                                  }
                                  checkClassName="text-edit-light"
                                />
                              </button>
                            ))}
                          </div>,
                          document.body
                        )}
                    </div>
                  )}
                </div>
                )}
                </>
                )}

                {isSelfEdit && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <Lock className="w-3 h-3" />
                      Project Memberships
                    </Label>
                    <p className="text-xs text-gray-500">
                      Projects you&apos;re assigned to. Contact an Admin or
                      Maintainer in your organization to be added or removed.
                    </p>
                    {selfProjectsLoading ? (
                      <LoadingIndicator
                        label="Loading your projects..."
                        className="px-1 py-3 text-gray-500"
                      />
                    ) : selfProjectsLoadError ? (
                      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        {selfProjectsLoadError}
                      </div>
                    ) : selfProjects.length === 0 ? (
                      <Input
                        value="You are not assigned to any projects"
                        disabled
                        className="bg-gray-100 text-gray-500 italic cursor-not-allowed"
                      />
                    ) : (
                      <OnboardingNestedPanel
                        ref={selfProjectsScrollRef}
                        className="bg-gray-100 max-h-72 divide-y divide-gray-200 overflow-y-auto"
                      >
                        {selfProjects.map((project) => (
                          <div
                            key={project.id}
                            className="px-4 py-3 flex items-center gap-3"
                          >
                            <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center shrink-0">
                              <Briefcase className="w-5 h-5 text-brand" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 break-words">
                                {project.name}
                              </p>
                              <p className="text-sm text-gray-500 break-words">
                                {project.customer}
                              </p>
                            </div>
                          </div>
                        ))}
                        <div
                          ref={selfProjectsSentinelRef}
                          className="flex justify-center px-4 py-3"
                        >
                          {selfProjectsLoadingMore && (
                            <LoadingSpinner size="sm" className="text-brand" />
                          )}
                        </div>
                      </OnboardingNestedPanel>
                    )}
                  </div>
                )}

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">
                    Contact Information
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    {editingOwnMembership
                      ? 'First name, last name, and phone are editable on your own account.'
                      : 'First name, last name, and phone are editable by you. Email, organization, and role changes require an Admin or Maintainer.'}
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="firstName"
                        className="flex items-center gap-1.5"
                      >
                        <Pencil className="w-3 h-3 text-edit-light" />
                        First Name
                      </Label>
                      <Input
                        id="firstName"
                        placeholder="Enter first name (optional)"
                        value={formData.firstName}
                        onChange={(e) => {
                          clearFieldErrors('firstName');
                          setFormData({
                            ...formData,
                            firstName: e.target.value,
                          });
                        }}
                        className={cn(errors.firstName && 'border-red-500')}
                      />
                      {errors.firstName && (
                        <p className="text-sm text-red-500">
                          {errors.firstName}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="lastName"
                        className="flex items-center gap-1.5"
                      >
                        <Pencil className="w-3 h-3 text-edit-light" />
                        Last Name
                      </Label>
                      <Input
                        id="lastName"
                        placeholder="Enter last name (optional)"
                        value={formData.lastName}
                        onChange={(e) => {
                          clearFieldErrors('lastName');
                          setFormData({
                            ...formData,
                            lastName: e.target.value,
                          });
                        }}
                        className={cn(errors.lastName && 'border-red-500')}
                      />
                      {errors.lastName && (
                        <p className="text-sm text-red-500">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <Label
                      htmlFor="phone"
                      className="flex items-center gap-1.5"
                    >
                      <Pencil className="w-3 h-3 text-edit-light" />
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="Enter phone number (optional)"
                      value={formData.phone}
                      onChange={(e) => {
                        clearFieldErrors('phone');
                        setFormData({ ...formData, phone: e.target.value });
                      }}
                      className={cn(errors.phone && 'border-red-500')}
                    />
                    {errors.phone && (
                      <p className="text-sm text-red-500">{errors.phone}</p>
                    )}
                  </div>

                  <div className="mt-4 space-y-2">
                    <Label
                      htmlFor="email"
                      className="flex items-center gap-1.5"
                    >
                      <Lock className="w-3 h-3" />
                      Email
                    </Label>
                    <Input
                      id="email"
                      value={selectedUser.email}
                      disabled
                      className="bg-gray-100 text-gray-500 cursor-not-allowed"
                    />
                  </div>
                </div>

                {!isSelfEdit && editingPlatformAdminRow && editingAdminRole && (
                  <div className="border-t border-gray-200 pt-6">
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      {selectedIsPlatformAdmin
                        ? 'This account is an Admin. Change role above to assign a Maintainer or Developer role.'
                        : 'Admin access is global.'}{' '}
                      Organization memberships and project assignments are not
                      used while Admin access is active.
                    </p>
                  </div>
                )}

                {!isSelfEdit && !editingAdminRole && (
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <Pencil className="w-3 h-3 text-edit-light" />
                    Project Assignments
                  </h3>

                  {demotingFromAdmin && !hasResolvedEditOrganization ? (
                    <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      Select an organization above to configure access for this
                      membership. Project assignments will appear once an
                      organization is chosen.
                    </p>
                  ) : editingMaintainer ? (
                    <p className="text-sm text-sky-900 bg-sky-50 border border-sky-200 rounded-md px-3 py-2">
                      This user is a{' '}
                      <span className="font-medium">Maintainer</span> in{' '}
                      <span className="font-medium">
                        {resolvedEditOrganization}
                      </span>
                      . Maintainers can access and manage all projects in their
                      organization without individual project assignments.
                    </p>
                  ) : (
                  <>
                  <p className="text-xs text-gray-500 mb-4">
                    Manage which projects in{' '}
                    <span className="font-medium">
                      {resolvedEditOrganization}
                    </span>{' '}
                    this user belongs to. Editable by Admin or Maintainer.
                  </p>

                  {projectsLoadError ? (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      {projectsLoadError}
                    </div>
                  ) : projectsLoading && orgProjectsForEdit.length === 0 ? (
                    <LoadingIndicator
                      label={
                        editProjectsTotalCount > 0
                          ? `Loading projects… (${orgProjectsForEdit.length} of ${editProjectsTotalCount})`
                          : 'Loading projects…'
                      }
                      className="px-1 py-3 text-gray-500"
                    />
                  ) : (
                    <>
                      {(projectsLoadingMore || projectsHasMore) &&
                        editProjectsTotalCount > 0 && (
                          <p className="mb-2 text-xs text-gray-500">
                            {projectsLoadingMore
                              ? `Loading projects… (${orgProjectsForEdit.length} of ${editProjectsTotalCount})`
                              : `Showing ${orgProjectsForEdit.length} of ${editProjectsTotalCount} projects`}
                          </p>
                        )}
                    <OnboardingNestedPanel
                      ref={editProjectsScrollRef}
                      className="max-h-72 divide-y divide-gray-200 overflow-y-auto"
                    >
                      {assignableProjects.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-gray-500">
                          No projects found for{' '}
                          <span className="font-medium text-gray-700">
                            {resolvedEditOrganization}
                          </span>
                          . None exist for this organization yet.
                        </div>
                      ) : (
                        assignableProjects.map((project) => {
                          const projectId = normalizeProjectId(project.id);
                          const isSelected = selectedProjectsEdit.some(
                            (id) => normalizeProjectId(id) === projectId,
                          );
                          const wasSelected = userProjects.some(
                            (id) => normalizeProjectId(id) === projectId,
                          );
                          return (
                            <button
                              key={project.id}
                              type="button"
                              onClick={() =>
                                toggleProjectAssignmentEdit(projectId)
                              }
                              className={cn(
                                'w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors',
                                isSelected && 'bg-edit-muted',
                              )}
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center shrink-0">
                                  <Briefcase className="w-5 h-5 text-brand" />
                                </div>
                                <div className="min-w-0 text-left">
                                  <p className="font-medium text-gray-900 break-words">
                                    {project.name}
                                  </p>
                                  {isAdmin && (
                                    <p className="text-sm text-gray-500 break-words">
                                      {project.organization}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {isSelected !== wasSelected && (
                                  <span
                                    className={cn(
                                      'px-2 py-0.5 rounded text-xs font-medium',
                                      isSelected
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-red-100 text-red-700',
                                    )}
                                  >
                                    {isSelected ? 'Will add' : 'Will remove'}
                                  </span>
                                )}
                                <div
                                  className={cn(
                                    'w-5 h-5 rounded border-2 flex items-center justify-center',
                                    isSelected
                                      ? 'bg-edit border-edit'
                                      : 'border-gray-300',
                                  )}
                                >
                                  {isSelected && (
                                    <Check className="w-3 h-3 text-white" />
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })
                      )}
                      <div
                        ref={editProjectsSentinelRef}
                        className="flex justify-center px-4 py-3"
                      >
                        {projectsLoadingMore && (
                          <LoadingSpinner size="sm" className="text-brand" />
                        )}
                      </div>
                    </OnboardingNestedPanel>
                    </>
                  )}
                  </>
                  )}
                </div>
                )}

              </div>

              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={
                    isSelfEdit
                      ? () => navigate(pageRoutes.onboarding)
                      : handleBack
                  }
                >
                  {isSelfEdit ? 'Cancel' : 'Back'}
                </Button>
                <Button onClick={handleNext}>Continue</Button>
              </div>
            </OnboardingSurface>
          )}

          {showReview && selectedUser && (
            <OnboardingSurface className="p-8">
              <h2
                className="text-xl font-bold text-brand mb-2 uppercase"
                aria-label={
                  isSelfEdit
                    ? 'Step 2: Review and Save'
                    : 'Step 3: Review and Save'
                }
              >
                {isSelfEdit
                  ? 'Step 2: Review & Save'
                  : 'Step 3: Review \u0026 Save'}
              </h2>
              <p className="text-gray-600 text-sm mb-8">
                Review the changes before saving.
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-brand rounded-full flex items-center justify-center text-white font-medium shrink-0">
                  {getUserInitials(formData.firstName, formData.lastName)}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 break-words">
                    {formatUserDisplayName(
                      formData.firstName,
                      formData.lastName
                    )}
                  </p>
                  <p className="text-sm text-gray-500 break-all">
                    {selectedUser.email}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700">
                  Changes Summary
                </h3>
                <p className="text-xs text-gray-500">
                  <Pencil className="w-3 h-3 inline mr-1 text-edit-light" />{' '}
                  indicates editable fields
                </p>

                <OnboardingNestedPanel className="divide-y divide-gray-200">
                  {isAdmin && demotingFromAdmin && !editingOwnMembership && (
                    <DiffRow
                      label="Organization"
                      adminOnly
                      oldValue="Global (Admin)"
                      newValue={formData.organization || 'Not set'}
                      changed
                    />
                  )}
                  {(isAdmin || isMaintainer) &&
                    initialMembershipRole != null &&
                    !editingOwnMembership && (
                    <DiffRow
                      label="Role"
                      adminOnly={isAdmin}
                      oldValue={formatPlatformRoleLabel(initialMembershipRole)}
                      newValue={formatPlatformRoleLabel(formData.platformRole)}
                      changed={
                        formData.platformRole !== initialMembershipRole
                      }
                    />
                  )}
                  <DiffRow
                    label="First Name"
                    selfOrAdmin
                    oldValue={selectedUser.firstName.trim() || 'Not provided'}
                    newValue={formData.firstName.trim() || 'Not provided'}
                    changed={formData.firstName !== selectedUser.firstName}
                  />
                  <DiffRow
                    label="Last Name"
                    selfOrAdmin
                    oldValue={selectedUser.lastName.trim() || 'Not provided'}
                    newValue={formData.lastName.trim() || 'Not provided'}
                    changed={formData.lastName !== selectedUser.lastName}
                  />
                  <DiffRow
                    label="Phone Number"
                    selfOrAdmin
                    oldValue={selectedUser.phone || 'Not set'}
                    newValue={formData.phone || 'Not set'}
                    changed={formData.phone !== (selectedUser.phone || '')}
                  />
                </OnboardingNestedPanel>

                {hasProjectChanges && (
                  <OnboardingNestedPanel className="p-4">
                    <p className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-1.5">
                      <Pencil className="w-3 h-3 text-edit-light" />
                      Project Assignments
                      <span className="text-xs text-edit-light">
                        (Admin or Maintainer)
                      </span>
                    </p>
                    {projectsToAdd.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-green-700 mb-1">
                          Will be added to:
                        </p>
                        <ul className="space-y-1">
                          {projectsToAdd.map((projectId) => {
                            const project = assignableProjects.find(
                              (p) => p.id === projectId,
                            );
                            return (
                              <li
                                key={projectId}
                                className="flex items-center gap-2 text-sm text-gray-700"
                              >
                                <Briefcase className="w-4 h-4 text-brand shrink-0" />
                                <span className="font-medium">
                                  {project?.name ?? projectId}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                    {projectsToRemove.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-red-700 mb-1">
                          Will be removed from:
                        </p>
                        <ul className="space-y-1">
                          {projectsToRemove.map((projectId) => {
                            const project = assignableProjects.find(
                              (p) => p.id === projectId,
                            );
                            return (
                              <li
                                key={projectId}
                                className="flex items-center gap-2 text-sm text-gray-700"
                              >
                                <Briefcase className="w-4 h-4 text-brand shrink-0" />
                                <span className="font-medium line-through text-gray-400">
                                  {project?.name ?? projectId}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </OnboardingNestedPanel>
                )}

                {!hasChanges && !submitting && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      No changes have been made to this user&apos;s details.
                    </p>
                  </div>
                )}
              </div>

              {submitError && (
                <div className="mt-6 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {submitError}
                </div>
              )}

              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={submitting}
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="bg-edit hover:bg-edit-hover text-white"
                  disabled={!hasChanges || submitting}
                >
                  {submitting ? (
                    <span className="inline-flex items-center gap-2">
                      <LoadingSpinner size="sm" />
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </OnboardingSurface>
          )}
        </div>
      </main>

      <Dialog
        open={pendingRemoveOrganization != null}
        onClose={() => setPendingRemoveOrganization(null)}
        labelledBy="remove-membership-title"
        describedBy="remove-membership-description"
        panelClassName="max-w-md"
      >
        <h2
          id="remove-membership-title"
          className="text-lg font-bold text-brand"
        >
          Remove organization membership?
        </h2>
        <p
          id="remove-membership-description"
          className="mt-3 text-sm text-gray-600"
        >
          Remove{' '}
          <span className="font-semibold">
            {formatUserDisplayName(
              selectedUser?.firstName ?? '',
              selectedUser?.lastName ?? '',
            )}
          </span>{' '}
          from{' '}
          <span className="font-semibold">{pendingRemoveOrganization}</span>?
          This also revokes their project access in that organization.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => setPendingRemoveOrganization(null)}
            disabled={membershipRemoving != null}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-red-600 text-white hover:bg-red-700"
            disabled={membershipRemoving != null}
            onClick={() => {
              if (pendingRemoveOrganization) {
                void handleRemoveMembership(pendingRemoveOrganization);
              }
            }}
          >
            {membershipRemoving ? 'Removing…' : 'Remove membership'}
          </Button>
        </div>
      </Dialog>
    </>
  );
}

interface DiffRowProps {
  label: string;
  oldValue: string;
  newValue: string;
  changed: boolean;
  adminOnly?: boolean;
  selfOrAdmin?: boolean;
  capitalize?: boolean;
}

function DiffRow({
  label,
  oldValue,
  newValue,
  changed,
  adminOnly,
  selfOrAdmin,
  capitalize,
}: DiffRowProps) {
  const hintLabel = adminOnly
    ? '(Admin only)'
    : selfOrAdmin
    ? '(Self or Admin)'
    : '';

  return (
    <div className="p-4 min-w-0">
      <p className="text-sm font-medium text-gray-500 mb-1 flex flex-wrap items-center gap-1.5">
        <Pencil className="w-3 h-3 text-edit-light shrink-0" />
        {label}
        {hintLabel && (
          <span className="text-xs text-edit-light">{hintLabel}</span>
        )}
      </p>
      {/* `flex-wrap` lets long values (e.g. emails) drop to a new line
          instead of overflowing horizontally; `break-all` lets each value
          itself wrap when it has no whitespace. */}
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <span
          className={cn(
            'break-all min-w-0',
            capitalize ? 'capitalize' : 'text-gray-900',
            changed && 'line-through text-gray-400'
          )}
        >
          {oldValue}
        </span>
        {changed && (
          <>
            <span className="text-gray-400 shrink-0">&rarr;</span>
            <span
              className={cn(
                'text-edit font-medium break-all min-w-0',
                capitalize && 'capitalize'
              )}
            >
              {newValue}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

export default EditUser;
